// websocketService.js
class WebSocketService {
  /**
   * Initializes the WebSocket service with the given URL and options.
   * @param {string} url - The WebSocket server URL to connect to.
   * @param {Object} options - Optional configuration properties.
   * @param {number} options.reconnectInterval - Base interval for reconnection attempts (in ms).
   * @param {number} options.maxReconnectAttempts - Maximum number of reconnection attempts.
   * @param {number} options.maxReconnectInterval - Maximum interval between reconnection attempts (in ms).
   */
  constructor (url, options = {}) {
    this.url = url
    this.reconnectInterval = options.reconnectInterval || 1000 // Base interval in ms
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5
    this.maxReconnectInterval = options.maxReconnectInterval || 30000 // 30 seconds
    this.reconnectAttempts = 0
    this.socket = null
    this.eventHandlers = {}
    this.messageQueue = []
    this.isConnected = false // Track connection state
  }

  /**
   * Initializes the connection to the WebSocket server.
   */
  initialize () {
    this.connect()
  }

  /**
   * Establishes a WebSocket connection to the server.
   * Handles various connection events and sets up error handling.
   */
  connect () {
    this.socket = new WebSocket(this.url)

    this.socket.onopen = () => {
      console.log('[WebsocketService] WebSocket connected')
      this.isConnected = true
      this.reconnectAttempts = 0
      this.triggerEvent('open')
      this.flushMessageQueue() // Send any queued messages
    }

    this.socket.onclose = (event) => {
      console.warn('WebSocket disconnected:', event.reason)
      this.isConnected = false
      this.triggerEvent('close', { reason: event.reason, wasClean: event.wasClean })
      this.handleReconnect(event)
    }

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error)
      this.triggerEvent('error', { message: 'WebSocket error occurred', error })
      this.socket.close() // Attempt to close the socket on error
    }

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.triggerEvent('message', data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
        this.triggerEvent('error', { message: 'Failed to parse message', error })
      }
    }
  }

  /**
   * Handles reconnection logic when the WebSocket disconnects.
   * @param {CloseEvent} event - The close event to analyze.
   */
  handleReconnect (event) {
    if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
      const nextInterval = Math.min(
        this.reconnectInterval * Math.pow(2, this.reconnectAttempts),
        this.maxReconnectInterval
      )
      console.log(`[WebsocketService] Attempting to reconnect in ${nextInterval / 1000} seconds...`)
      setTimeout(() => this.connect(), nextInterval)
      this.reconnectAttempts++
    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      this.triggerEvent('reconnect_failed', { reason: event.reason, attempts: this.reconnectAttempts })
    }
  }

  /**
   * Sends a message to the WebSocket server.
   * @param {any} data - The message to send, which will be stringified to JSON.
   * @throws {Error} If the message is not serializable to JSON.
   */
  send (data) {
    try {
      if (this.isConnected) {
        this.socket.send(JSON.stringify(data))
        console.debug(JSON.stringify(data))
      } else {
        console.warn('WebSocket not open, message queued')
        this.messageQueue.push(data) // Queue the message if the connection is not open
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      this.triggerEvent('error', { message: 'Failed to send message', error })
    }
  }

  /**
   * Sends all messages in the queue when the WebSocket connection is open.
   */
  flushMessageQueue () {
    while (this.messageQueue.length > 0 && this.isConnected) {
      const data = this.messageQueue.shift()
      this.send(data) // Use the send method to handle potential errors
    }
  }

  /**
   * Closes the WebSocket connection if it exists.
   */
  close () {
    if (this.socket) {
      this.socket.close()
      console.log('[WebsocketService] WebSocket connection closed.')
    }
  }

  /**
   * Registers an event handler for a specific event.
   * @param {string} event - The name of the event to listen for (e.g., 'message', 'error').
   * @param {function} handler - The callback function to execute when the event occurs.
   */
  on (event, handler) {
    if (!this.eventHandlers[event]) this.eventHandlers[event] = []
    this.eventHandlers[event].push(handler)
  }

  /**
   * Unregisters an event handler for a specific event.
   * @param {string} event - The name of the event to stop listening for.
   * @param {function} handler - The callback function to remove from the event listeners.
   */
  off (event, handler) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event] = this.eventHandlers[event].filter(h => h !== handler)
    }
  }

  /**
   * Triggers all registered handlers for a specific event with the provided data.
   * @param {string} event - The name of the event to trigger.
   * @param {any} data - The data to pass to the event handlers.
   */
  triggerEvent (event, data) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].forEach(handler => handler(data))
    }
  }
}

export default WebSocketService
